package com.partejar.app.feature.gestantes.presentation

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.foundation.rememberScrollState
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.listSaver
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.semantics.*
import androidx.compose.ui.platform.LocalFocusManager
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.format.parseClockTime
import com.partejar.app.core.ui.components.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.gestantes.data.*

internal data class ExamFormErrors(val type: String? = null, val date: String? = null, val time: String? = null) {
    val valid get() = type == null && date == null && time == null
}

internal fun validateExamForm(typeId: Long?, date: String, time: String, customName: String = ""): ExamFormErrors = ExamFormErrors(
    type = if (typeId == null && customName.isBlank()) "Selecione uma sugestão ou informe o nome do exame." else null,
    date = when { date.isBlank() -> "Informe a data do exame."; parseBrazilianDate(date) == null -> "Informe uma data válida no formato dd/MM/aaaa."; else -> null },
    time = if (parseClockTime(time) == null) "Informe uma hora válida no formato HH:mm." else null
)

internal data class ExamResultErrors(val parameter: String? = null, val value: String? = null) {
    val valid get() = parameter == null && value == null
}

internal fun validateExamResult(parameter: String, value: String) = ExamResultErrors(
    parameter = if (parameter.isBlank()) "Informe o parâmetro do resultado." else null,
    value = if (value.isBlank()) "Informe o valor do resultado." else null
)

internal fun saveInitialExamResults(results: List<GestanteExamResultInput>): List<String> =
    listOf("catalog-v1") + results.flatMap {
        listOf(it.parameter, it.value, it.unit.orEmpty(), it.reference.orEmpty(), it.status.name,
            it.analysisId?.toString().orEmpty(), it.analysisUnitId?.toString().orEmpty(), it.freeResult?.toString().orEmpty())
    }

internal fun restoreInitialExamResults(values: List<String>): List<GestanteExamResultInput> =
    if (values.firstOrNull() == "catalog-v1") values.drop(1).chunked(8).map {
        GestanteExamResultInput(it[0], it[1], it[2].ifBlank { null }, it[3].ifBlank { null },
            GestanteExamStatus.valueOf(it[4]), it[5].toLongOrNull(), it[6].toLongOrNull(), it[7].toBooleanStrictOrNull())
    } else values.chunked(5).map {
        GestanteExamResultInput(it[0], it[1], it[2].ifBlank { null }, it[3].ifBlank { null }, GestanteExamStatus.valueOf(it[4]))
    }

private val InitialResultsSaver = listSaver<List<GestanteExamResultInput>, String>(
    save = { results -> saveInitialExamResults(results) }, restore = { values -> restoreInitialExamResults(values) }
)

@Composable
internal fun RegisterExamForm(types: List<GestanteExamType>, saving: Boolean, error: String?, onSave: (GestanteExamInput) -> Unit, onCancel: () -> Unit, interactionsEnabled: Boolean = true) {
    val spacing = PartejarTheme.spacing
    val editable = !saving && interactionsEnabled
    var examTypeName by rememberSaveable { mutableStateOf(types.firstOrNull { it.active }?.name?.replace('_', ' ').orEmpty()) }
    var date by rememberSaveable { mutableStateOf("") }
    var time by rememberSaveable { mutableStateOf("") }
    var observation by rememberSaveable { mutableStateOf("") }
    var results by rememberSaveable(stateSaver = InitialResultsSaver) { mutableStateOf(emptyList<GestanteExamResultInput>()) }
    var addingInitial by rememberSaveable { mutableStateOf(false) }
    val resultVisibility = remember { MutableTransitionState(addingInitial) }
    val focus = LocalFocusManager.current
    var pendingResult by remember { mutableStateOf<GestanteExamResultInput?>(null) }
    LaunchedEffect(resultVisibility.isIdle, resultVisibility.currentState) {
        if (addingInitial && resultVisibility.isIdle && !resultVisibility.currentState && !resultVisibility.targetState) {
            pendingResult?.let { results = results + it }
            pendingResult = null
            addingInitial = false
        }
    }
    var attempt by rememberSaveable { mutableStateOf(0) }
    val selectedType = types.firstOrNull {
        it.active && it.name.replace('_', ' ').equals(examTypeName.trim(), ignoreCase = true)
    }
    val customName = if (selectedType == null) examTypeName.trim() else ""
    val errors = validateExamForm(selectedType?.id, date, time, customName)
    val typeFocus = remember { BringIntoViewRequester() }
    val dateFocus = remember { BringIntoViewRequester() }
    val timeFocus = remember { BringIntoViewRequester() }
    LaunchedEffect(attempt) {
        if (attempt > 0) when { errors.type != null -> typeFocus.bringIntoView(); errors.date != null -> dateFocus.bringIntoView(); errors.time != null -> timeFocus.bringIntoView() }
    }
    FormScroll {
        ExamSectionTitle("DADOS DO EXAME")
        Text("Registre a realização do exame. Os resultados podem ser adicionados agora ou depois.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        Column(Modifier.bringIntoViewRequester(typeFocus), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
            ExamTypePicker(
                types = types.filter { it.active },
                value = examTypeName,
                enabled = editable && !addingInitial && results.isEmpty(),
                onValueChange = { examTypeName = it.take(255) }
            )
            if (results.isNotEmpty()) Text("O tipo permanece vinculado aos resultados incluídos.", style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant)
            FieldError(if (attempt > 0) errors.type else null)
        }
        Column(Modifier.bringIntoViewRequester(dateFocus), verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
            PartejarDateField(date, { date = it }, "Data do exame", enabled = editable, error = if (attempt > 0) errors.date else null)
        }
        Column(Modifier.bringIntoViewRequester(timeFocus)) {
            PartejarTimeField(time, { time = it }, label = "Horário do exame", enabled = editable,
                error = if (attempt > 0) errors.time else null)
        }
        PartejarTextField(observation, { observation = it }, label = "Observação (opcional)", enabled = editable, singleLine = false, minLines = 2)
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        ExamSectionTitle("RESULTADOS INICIAIS · OPCIONAL")
        results.forEach { result ->
            PartejarCard(Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                    Text(result.parameter, style = MaterialTheme.typography.titleMedium)
                    Text(listOfNotNull(numericExamDisplay(result.value), result.unit).joinToString(" "))
                    result.reference?.let { Text("Referência: $it", color = MaterialTheme.colorScheme.onSurfaceVariant) }
                    PartejarStatusBadge(result.status.toExamLabel(), tone = result.status.toBadgeTone())
                }
            }
        }
        if (addingInitial) {
            PartejarVisibility(resultVisibility) {
                PartejarCard(Modifier.fillMaxWidth()) {
                    ResultEditorFields(initial = null, saving = saving, error = null, saveLabel = "Incluir resultado no exame",
                        onSave = { pendingResult = it; focus.clearFocus(); resultVisibility.targetState = false },
                        onCancel = { focus.clearFocus(); resultVisibility.targetState = false },
                        interactionsEnabled = interactionsEnabled && resultVisibility.targetState,
                        analyses = selectedType?.analyses.orEmpty())
                }
            }
        } else ExamAction("Adicionar resultado", PartejarIcons.Actions.add, {
            focus.clearFocus(); addingInitial = true; resultVisibility.targetState = true
        }, enabled = editable && (selectedType != null || customName.isNotBlank()), secondary = true)
        if (results.isNotEmpty() && !addingInitial) Text("${results.size} resultado(s) incluído(s). Serão enviados ao salvar o exame.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        if (addingInitial) Text("Conclua ou cancele o resultado em edição antes de salvar o exame.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        FormActionError(error)
        ExamAction("Salvar exame", PartejarIcons.Feedback.success, {
            attempt++
            if (errors.valid && !addingInitial) onSave(GestanteExamInput(selectedType?.id, requireNotNull(examDateTimeInput(date, time)), observation.ifBlank { null }, results, customName.ifBlank { null }))
        }, enabled = editable && !addingInitial, saving = saving)
        ExamAction("Cancelar", PartejarIcons.Actions.back, onCancel, enabled = editable, secondary = true)
    }
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
private fun ExamTypePicker(types: List<GestanteExamType>, value: String, enabled: Boolean, onValueChange: (String) -> Unit) {
    var expanded by remember { mutableStateOf(false) }
    val query = value.trim()
    val filteredTypes = types.filter { type ->
        query.isBlank() || type.name.replace('_', ' ').contains(query, ignoreCase = true)
    }
    val selectedId = types.firstOrNull {
        it.name.replace('_', ' ').equals(query, ignoreCase = true)
    }?.id
    val menuExpanded = expanded && filteredTypes.isNotEmpty()
    ExposedDropdownMenuBox(
        expanded = menuExpanded,
        onExpandedChange = { expanded = it },
        modifier = Modifier.fillMaxWidth()
    ) {
        PartejarTextField(
            value = value,
            onValueChange = { onValueChange(it); expanded = true },
            label = "Tipo de exame",
            modifier = Modifier.menuAnchor(
                type = ExposedDropdownMenuAnchorType.PrimaryEditable,
                enabled = enabled && types.isNotEmpty()
            ),
            enabled = enabled,
            placeholder = "Selecione ou digite outro exame",
            supportingText = if (types.isEmpty()) "Sem sugestões no catálogo; informe o nome do exame." else
                "Digite para filtrar as sugestões ou informe outro exame.",
            trailingIcon = if (types.isNotEmpty()) {
                {
                    Icon(
                        imageVector = PartejarIcons.Actions.next,
                        contentDescription = null,
                        modifier = Modifier.rotate(if (menuExpanded) 270f else 90f)
                    )
                }
            } else null
        )
        ExposedDropdownMenu(expanded = menuExpanded, onDismissRequest = { expanded = false }) {
            filteredTypes.forEach { type ->
                val displayName = type.name.replace('_', ' ')
                DropdownMenuItem(
                    text = { Text(displayName) },
                    onClick = { onValueChange(displayName); expanded = false },
                    modifier = Modifier.semantics { this.selected = selectedId == type.id },
                    leadingIcon = { if (selectedId == type.id) Icon(PartejarIcons.Feedback.success, null) }
                )
            }
        }
    }
}

@Composable
internal fun ExamResultForm(initial: GestanteExamResult?, saving: Boolean, error: String?, onSave: (GestanteExamResultInput) -> Unit, onCancel: () -> Unit, interactionsEnabled: Boolean = true, analyses: List<GestanteExamAnalysis> = emptyList()) {
    FormScroll { ResultEditorFields(initial, saving, error, "Salvar resultado", onSave, onCancel, interactionsEnabled, analyses) }
}

@Composable
private fun ResultEditorFields(initial: GestanteExamResult?, saving: Boolean, error: String?, saveLabel: String, onSave: (GestanteExamResultInput) -> Unit, onCancel: () -> Unit, interactionsEnabled: Boolean = true, analyses: List<GestanteExamAnalysis> = emptyList()) {
    val spacing = PartejarTheme.spacing
    val editable = !saving && interactionsEnabled
    var parameter by rememberSaveable(initial?.id) { mutableStateOf(initial?.parameter.orEmpty()) }
    var value by rememberSaveable(initial?.id) { mutableStateOf(initial?.value.orEmpty()) }
    var unit by rememberSaveable(initial?.id) { mutableStateOf(initial?.unit.orEmpty()) }
    var analysisId by rememberSaveable(initial?.id) { mutableStateOf(initial?.analysisId) }
    var unitId by rememberSaveable(initial?.id) { mutableStateOf(initial?.analysisUnitId) }
    var freeResult by rememberSaveable(initial?.id) { mutableStateOf<Boolean?>(
        if (initial == null || initial.analysisId != null) false else if (initial.freeResult) true else null
    ) }
    var reference by rememberSaveable(initial?.id) { mutableStateOf(initial?.reference.orEmpty()) }
    var statusName by rememberSaveable(initial?.id) { mutableStateOf((initial?.status ?: GestanteExamStatus.PENDING).name) }
    var attempt by rememberSaveable(initial?.id) { mutableStateOf(0) }
    val analysis = analyses.find { it.id == analysisId }
    val textual = freeResult == true || (initial != null && freeResult == null && analysisId == null)
    val selectionError = examCatalogSelectionError(analysisId, unitId, freeResult, analyses, initial)
    val errors = validateExamResult(parameter, value).copy(parameter = selectionError ?: if (textual && parameter.isBlank()) "Informe o nome do resultado." else null)
    val parameterFocus = remember { BringIntoViewRequester() }
    val valueFocus = remember { BringIntoViewRequester() }
    LaunchedEffect(attempt) {
        if (attempt > 0) when { errors.parameter != null -> parameterFocus.bringIntoView(); errors.value != null -> valueFocus.bringIntoView() }
    }
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
        ExamSectionTitle(if (initial == null) "NOVO RESULTADO" else "CORREÇÃO DO RESULTADO")
        Column(Modifier.bringIntoViewRequester(parameterFocus)) {
            ExamCatalogPicker(
                label = "Análise", selectedKey = analysisId?.toString() ?: if (freeResult == true) "other" else null,
                selectedLabel = when { analysisId != null -> parameter; freeResult == true -> "Outro resultado"; textual -> "Resultado legado"; else -> null },
                options = analyses.filter { it.active }.map { ExamCatalogChoice(it.id.toString(), it.name, it.definition) } +
                    ExamCatalogChoice("other", "Outro resultado", "Nome e unidade livres, sem evolução automática."),
                enabled = editable, searchable = true,
                onSelect = { choice ->
                    val nextId = choice.toLongOrNull()
                    if (nextId != analysisId || (choice == "other" && freeResult != true)) {
                        val next = analyses.find { it.id == nextId }
                        val defaultUnit = next?.units?.singleOrNull { it.active && it.default }
                        analysisId = nextId; unitId = defaultUnit?.id; freeResult = nextId == null
                        parameter = next?.name.orEmpty(); unit = defaultUnit?.label.orEmpty()
                        value = ""; reference = ""; statusName = GestanteExamStatus.PENDING.name
                    }
                }
            )
            if (analysis != null) Text(analysis.definition, style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.padding(top = spacing.space8))
            if (analysis?.active == false) Text("Análise inativa: disponível apenas para preservar esta correção.",
                style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
            FieldError(if (attempt > 0) errors.parameter else null)
        }
        // Same entrance/exit primitive as the existing result editor; reduced motion is respected.
        val textVisibility = remember { MutableTransitionState(textual) }
        LaunchedEffect(textual) { textVisibility.targetState = textual }
        if (textVisibility.currentState || textVisibility.targetState || !textVisibility.isIdle) PartejarVisibility(textVisibility) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing.space16)) {
                Text(if (freeResult == true) "Outro resultado não participa da evolução automática." else
                    "Registro legado: a comparação textual conservadora será preservada.", style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant)
                PartejarTextField(parameter, { parameter = it.take(255) }, label = "Nome do resultado", enabled = editable && textual,
                    isError = attempt > 0 && parameter.isBlank())
                PartejarTextField(unit, { unit = it.take(100) }, label = "Unidade livre (opcional)", enabled = editable && textual)
            }
        }
        if (analysis != null && (analysis.units.isNotEmpty() || unitId != null)) {
            ExamCatalogPicker(label = "Unidade", selectedKey = unitId?.toString() ?: "none",
                selectedLabel = unit.ifBlank { if (analysis.unitRequired) null else "Sem unidade" },
                options = analysis.units.filter { it.active || it.id == initial?.analysisUnitId }.map {
                    ExamCatalogChoice(it.id.toString(), it.label, if (it.default) "Unidade padrão" else if (!it.active) "Unidade inativa deste registro" else null)
                } + if (analysis.unitRequired) emptyList() else listOf(ExamCatalogChoice("none", "Sem unidade")),
                enabled = editable, onSelect = { choice ->
                    val nextId = choice.toLongOrNull()
                    if (nextId != unitId) {
                        unitId = nextId; unit = analysis.units.find { it.id == nextId }?.label.orEmpty()
                        value = ""; reference = ""; statusName = GestanteExamStatus.PENDING.name
                    }
                })
        } else if (analysis != null) Text("Unidade: não se aplica a esta análise.", style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant)
        Column(Modifier.bringIntoViewRequester(valueFocus)) {
            PartejarTextField(value, { value = it }, label = "Valor do resultado", enabled = editable, isError = attempt > 0 && errors.value != null,
                supportingText = "Pode ser numérico ou textual, conforme o laudo.")
            FieldError(if (attempt > 0) errors.value else null)
        }
        PartejarTextField(reference, { reference = it }, label = "Referência (opcional)", placeholder = "Conforme o laudo", enabled = editable, singleLine = false, minLines = 2)
        Text("Avaliação registrada", style = MaterialTheme.typography.labelLarge)
        Text("Selecione a avaliação informada. O app não interpreta os valores automaticamente.", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
        // Vertical choices remain readable with accessibility font sizes; no full-width buttons in a Row.
        GestanteExamStatus.entries.forEach { status ->
            PartejarCard(onClick = { statusName = status.name }, enabled = editable, motionEnabled = true,
                modifier = Modifier.fillMaxWidth().semantics { selected = statusName == status.name }) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(spacing.space12), verticalAlignment = Alignment.CenterVertically) {
                    RadioButton(selected = statusName == status.name, onClick = null, enabled = editable)
                    Text(status.toExamLabel(), Modifier.weight(1f))
                }
            }
        }
        FormActionError(error)
        ExamAction(saveLabel, PartejarIcons.Feedback.success, {
            attempt++
            if (errors.valid) onSave(GestanteExamResultInput(parameter.trim(), value.trim(), unit.trim().ifBlank { null }, reference.trim().ifBlank { null },
                GestanteExamStatus.valueOf(statusName), analysisId, unitId, freeResult))
        }, enabled = editable, saving = saving)
        ExamAction("Cancelar", PartejarIcons.Actions.back, onCancel, enabled = editable, secondary = true)
    }
}

@Composable
private fun FormScroll(content: @Composable ColumnScope.() -> Unit) {
    Column(Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(PartejarTheme.spacing.space16), verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space16), content = content)
}

@Composable
private fun FieldError(message: String?) {
    ExamFeedbackVisibility(message != null) {
        Text(message.orEmpty(), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.padding(top = PartejarTheme.spacing.space4).semantics { liveRegion = LiveRegionMode.Polite })
    }
}

@Composable
private fun FormActionError(message: String?) {
    val requester = remember { BringIntoViewRequester() }
    LaunchedEffect(message) { if (message != null) requester.bringIntoView() }
    Box(Modifier.bringIntoViewRequester(requester)) {
        ExamFeedbackVisibility(message != null) { if (message != null) ExamAlert(message, error = true) }
    }
}
